

















































import Vue from 'vue';
import DvFilterPopover from '@/components/util/DvFilterPopover.vue';
import JobProvider from '@/api/JobProvider';
import IJob from '@/models/jobs/IJob';
import { buildJobIndex, subtreeOf, IJobIndex } from '@/util/skillJobFilter';
import { Parent } from '@/util/filterChips';

interface IData {
    jobs: IJob[];
    idx: IJobIndex;
    byId: Record<number, IJob>;
}

export default Vue.extend({
    components: { DvFilterPopover },
    props: {
        value: { type: Array as () => number[], default: () => [] },
        inherit: { type: Boolean, default: true },
    },
    data(): IData {
        return {
            jobs: [],
            idx: { parentOf: {}, childrenOf: {} },
            byId: {},
        };
    },
    created() {
        this.load();
    },
    computed: {
        baseJobs(): IJob[] {
            return this.jobs.filter((j) => !j.parentJobId);
        },
        selectedSet(): Set<number> {
            return new Set(this.value);
        },
    },
    methods: {
        async load() {
            try {
                const jobs = await JobProvider.getAllJobs();
                this.jobs = jobs;
                this.idx = buildJobIndex(jobs);
                const byId: Record<number, IJob> = {};
                for (const j of jobs) {
                    byId[j.id] = j;
                }
                this.byId = byId;
                this.emitReady();
            } catch (e) {
                //  The popover self-hides while baseJobs is empty, so a failed
                //  load degrades to "no class filter" rather than a broken one.
                console.error('Failed to load jobs for the skill class filter', e);
            }
        },
        //  Emitted once, so the parent can label and collapse class chips
        //  without reaching into $refs. Every tier-1 and tier-2 job is offered
        //  as a collapsible parent; buildChips suppresses the nested one when
        //  a wider parent already covers it.
        emitReady() {
            const parents: Parent[] = [];
            const labels: Record<number, string> = {};
            for (const j of this.jobs) {
                labels[j.id] = this.jobName(j);
            }
            for (const j of this.jobs) {
                const children = this.idx.childrenOf[j.id] || [];
                if (children.length) {
                    parents.push({ label: this.jobName(j), childIds: subtreeOf(j.id, this.idx) });
                }
            }
            this.$emit('ready', { parents, labels });
        },
        jobName(j: IJob): string {
            return (j.name && j.name.message) || `Job ${j.id}`;
        },
        childrenOf(id: number): IJob[] {
            return (this.idx.childrenOf[id] || []).map((c) => this.byId[c]).filter((j) => !!j);
        },
        iconStyle(j: IJob): Record<string, string> {
            const icon = j.icon;
            if (!icon) {
                return {};
            }
            return {
                backgroundImage: `url(${icon.url})`,
                backgroundPosition: `-${icon.x}px -${icon.y}px`,
                width: `${icon.size}px`,
                height: `${icon.size}px`,
            };
        },
        isChecked(id: number): boolean {
            return this.selectedSet.has(id);
        },
        //  Some but not all of the branch is selected. Bound with .prop
        //  because `indeterminate` is a DOM property with no matching
        //  attribute, so a plain :indeterminate binding would do nothing.
        isPartial(id: number): boolean {
            if (this.selectedSet.has(id)) {
                return false;
            }
            return subtreeOf(id, this.idx).some((s) => this.selectedSet.has(s));
        },
        toggle(id: number, checked: boolean) {
            const sub = subtreeOf(id, this.idx);
            const set = new Set(this.value);
            for (const s of sub) {
                if (checked) {
                    set.add(s);
                } else {
                    set.delete(s);
                }
            }
            this.$emit('select', [...set].sort((a, b) => a - b));
        },
        emitAll() {
            this.$emit('select', this.jobs.map((j) => j.id).sort((a, b) => a - b));
        },
    },
});
